/**
 * useProjectInitializer
 *
 * Hook for initializing project data with API fallback.
 * Handles direct link access by fetching data from API when localStorage is empty.
 *
 * Flow:
 * 1. Check if project exists in localStorage store
 * 2. If exists → use existing data (fast path)
 * 3. If not → fetch from API (fallback for direct links)
 * 4. Hydrate stores with fetched data
 *
 * Usage:
 * ```tsx
 * const { loading, error, project } = useProjectInitializer(projectId)
 *
 * if (loading) return <LoadingOverlay />
 * if (error) return <ErrorDisplay error={error} />
 * // Use project data
 * ```
 */

import { useState, useEffect } from 'react'
import { useProjectStore } from '../store/projectStore'
import { useFloorStore } from '../store/floorStore'
import { mapDataService } from '../api/services/mapDataService'
import { log, LogCategory } from '../lib/logger'
import { storage } from '../lib/utils'
import type { ParkingLot } from '../store/projectStore'
import type { Floor } from '../store/floorStore'

interface ProjectInitializerResult {
  loading: boolean
  error: string | null
  project: ParkingLot | undefined
}

export function useProjectInitializer(
  projectId: string | undefined,
  backendProjectId?: string | undefined
): ProjectInitializerResult {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Store accessors - only subscribe to derived project to avoid effect re-run on other store updates
  const project = useProjectStore(state => state.getLotById(projectId || ''))

  useEffect(() => {
    async function initializeProject() {
      // No projectId provided
      if (!projectId) {
        log.warn(LogCategory.STATE, 'useProjectInitializer: No projectId provided')
        setError('No project ID provided')
        setLoading(false)
        return
      }

      // Project already exists in store - fast path
      if (project) {
        log.info(LogCategory.STATE, 'useProjectInitializer: Project found in store', { projectId })
        setLoading(false)
        setError(null)
        return
      }

      // Project not in store - fetch from API (fallback for direct links)
      log.info(LogCategory.STATE, 'useProjectInitializer: Project not in store, fetching from API', { projectId, backendProjectId })

      try {
        // Use backendProjectId if provided, otherwise try to parse projectId
        const apiProjectId = backendProjectId || projectId
        const projectIdNum = parseInt(apiProjectId, 10)
        if (isNaN(projectIdNum)) {
          throw new Error(`Invalid project ID: ${apiProjectId}. Backend project ID must be numeric.`)
        }

        // Fetch map data from API
        const response = await mapDataService.getMapDataParsed(projectIdNum)

        if (!response.success || !response.data || response.data.length === 0) {
          throw new Error('Project not found in API')
        }

        log.info(LogCategory.STATE, 'useProjectInitializer: Fetched map data from API', {
          projectId,
          floors: response.data.length
        })

        // Extract project information from first floor data
        const firstFloor = response.data[0] as any
        const projectName = firstFloor.map_data?.metadata?.name || `Project ${projectId}`
        const templateId = firstFloor.template_id

        // Check if project already exists in store (by ID or name)
        const existingProjectById = useProjectStore.getState().getLotById(projectId)
        const allProjects = useProjectStore.getState().lots
        const existingProjectByName = allProjects.find(p => p.name.toLowerCase() === projectName.toLowerCase())

        if (!existingProjectById && !existingProjectByName) {
          // Create project in store with API project ID
          const now = new Date().toISOString()
          const newProject: ParkingLot = {
            id: projectId, // Use API project ID instead of generating random ID
            name: projectName,
            description: firstFloor.map_data?.metadata?.description,
            templateId: templateId,
            created: now,
            modified: now,
          }

          log.info(LogCategory.STATE, 'useProjectInitializer: Creating project in store with API ID', newProject)

          // Directly add to store to preserve API ID
          const currentState = useProjectStore.getState()
          const newState = {
            lots: [...currentState.lots, newProject],
            currentLot: projectId,
          }

          // Persist to localStorage (matching projectStore pattern)
          storage.set('map-editor-projects', newState)

          useProjectStore.setState(newState)
        } else {
          log.info(LogCategory.STATE, 'useProjectInitializer: Project already exists in store, skipping creation', {
            projectId,
            existingById: !!existingProjectById,
            existingByName: !!existingProjectByName,
            projectName
          })
        }

        // Check if floors already exist for this project
        const existingFloors = useFloorStore.getState().getFloorsByLotId(projectId)
        const shouldCreateFloors = existingFloors.length === 0

        if (!shouldCreateFloors) {
          log.info(LogCategory.STATE, 'useProjectInitializer: Floors already exist, skipping floor creation', {
            projectId,
            floorCount: existingFloors.length
          })
          setError(null)
          setLoading(false)
          return
        }

        // Create floors in store
        log.info(LogCategory.STATE, 'useProjectInitializer: Creating floors from API data', {
          projectId,
          floorCount: response.data.length
        })

        const { addFloor, updateFloorMapData } = useFloorStore.getState()

        // Hydration 시 floor 이름 결정 우선순위:
        //   1) record.map_data.floors[i].name (저장 시 useSaveProject/useFloorAutoSave 가
        //      쓰는 nested 구조)
        //   2) record.map_data.name (per-record flat 구조 — 레거시 가능성)
        //   3) undefined → addFloor 가 폴백 자동 이름(B1, B2, ...) 생성
        // 두 구조 모두 방어적으로 시도해, 백엔드 스키마가 어느 쪽이든 사용자 입력 이름이 보존되게 한다.
        type AnyFloor = { name?: string; order?: number; graphJson?: any; [k: string]: unknown }
        type AnyMapData = {
          floors?: AnyFloor[]
          name?: string
          [k: string]: unknown
        }

        // 1) nested floors[] 가 있는 새 포맷이면 그쪽을 진실의 원천으로 사용.
        const firstRecordMapData = (response.data[0]?.map_data ?? null) as AnyMapData | null
        const nestedFloors = Array.isArray(firstRecordMapData?.floors) ? firstRecordMapData!.floors! : []

        if (nestedFloors.length > 0) {
          log.info(LogCategory.STATE, 'useProjectInitializer: Using nested floors[] from first record', {
            count: nestedFloors.length,
          })
          nestedFloors.forEach((floorEntry, index) => {
            const persistedName = typeof floorEntry?.name === 'string' && floorEntry.name.trim()
              ? floorEntry.name.trim()
              : undefined
            addFloor(projectId, persistedName ? { name: persistedName } : undefined)

            const floors = useFloorStore.getState().getFloorsByLotId(projectId)
            const newFloor = floors[floors.length - 1]
            if (newFloor) {
              const graphJson = (floorEntry as any)?.graphJson ?? { cells: [] }
              updateFloorMapData(newFloor.id, {
                graphJson: graphJson as any,
              })
              log.info(LogCategory.STATE, 'useProjectInitializer: Created floor (nested)', {
                floorId: newFloor.id,
                order: index,
                name: persistedName,
                graphJsonCellCount: (graphJson as any)?.cells?.length || 0,
              })
            }
          })
        } else {
          // 2) Legacy: record-per-floor — 각 record 가 하나의 floor 를 의미.
          response.data.forEach((floorData, index) => {
            const mapData = floorData.map_data as AnyMapData

            const persistedName = typeof mapData?.name === 'string' && mapData.name.trim()
              ? mapData.name.trim()
              : undefined

            addFloor(projectId, persistedName ? { name: persistedName } : undefined)

            const floors = useFloorStore.getState().getFloorsByLotId(projectId)
            const newFloor = floors[floors.length - 1]

            if (newFloor) {
              // Update floor with map data
              // If graphJson is null/undefined from server, initialize with empty structure so viewer can display the floor
              const graphJson = (mapData as any).graphJson || { cells: [] }

              updateFloorMapData(newFloor.id, {
                csvRawData: (mapData as any).csvRawData,
                csvFileName: (mapData as any).csvFileName,
                csvParsedData: (mapData as any).csvParsedData,
                csvGroupedLayers: (mapData as any).csvGroupedLayers,
                csvSelectedLayers: (mapData as any).csvSelectedLayers,
                metadata: (mapData as any).metadata,
                assets: (mapData as any).assets,
                objects: (mapData as any).objects,
                graphJson: graphJson as any,
                selectedElementId: (mapData as any).selectedElementId,
                renderScale: (mapData as any).renderScale,
              })

              log.info(LogCategory.STATE, 'useProjectInitializer: Created floor (legacy)', {
                floorId: newFloor.id,
                order: index,
                name: persistedName,
                hasGraphJson: !!(mapData as any).graphJson,
                graphJsonCellCount: (graphJson as any)?.cells?.length || 0
              })
            }
          })
        }

        log.info(LogCategory.STATE, 'useProjectInitializer: Successfully hydrated stores from API')
        setError(null)
        setLoading(false)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load project'
        log.error(LogCategory.STATE, 'useProjectInitializer: Failed to fetch project from API', {
          projectId,
          error: errorMessage
        })
        setError(errorMessage)
        setLoading(false)
      }
    }

    initializeProject()
  }, [projectId, project])

  return { loading, error, project }
}
